import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Trash2, Edit, ExternalLink, Link, QrCode, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { invalidateCache } from "@/utils/cacheManager";
import PhotoCropDialog from "@/components/PhotoCropDialog";
import PhotoPickerButtons from "@/components/PhotoPickerButtons";
import { compressImage } from "@/utils/compressImage";

import { BACKEND_URL } from '../../utils/apiBase';
const API = `${BACKEND_URL}/api`;
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('https://res.cloudinary.com/')) return url;
  return `${BACKEND_URL}${url}`;
};

export const JudgesTab = ({ judges, loadData, openEditJudge, uploadImage, slug, setQrJudge }) => {
  const { t } = useI18n();
  const [newJudge, setNewJudge] = useState({ judge_number: 1, first_name: "", pin_code: "" });
  const [judgeImageFile, setJudgeImageFile] = useState(null);
  const [judgeImagePreview, setJudgeImagePreview] = useState(null);
  const [pendingCropFile, setPendingCropFile] = useState(null);
  const baseUrl = window.location.origin;

  const handleJudgeImageChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || file.size <= 0) return;
    try {
      const compressed = await compressImage(file);
      if (!compressed || compressed.size <= 0) throw new Error("decode-failed");
      if (compressed.size > 10 * 1024 * 1024) { toast.error(t('athletes.fileTooLarge')); return; }
      toast.success(t('athletes.photoReady'));
      setPendingCropFile(compressed);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("photo accept failed", err);
      toast.error(t('athletes.onlyPngJpeg'));
    }
  };

  const handleCropConfirm = (croppedFile) => {
    setJudgeImagePreview((old) => {
      if (old && old.startsWith("blob:")) {
        try { URL.revokeObjectURL(old); } catch { /* no-op */ }
      }
      return URL.createObjectURL(croppedFile);
    });
    setJudgeImageFile(croppedFile);
    setPendingCropFile(null);
  };

  const handleDeletePhoto = () => {
    setJudgeImagePreview((old) => {
      if (old && typeof old === 'string' && old.startsWith('blob:')) {
        try { URL.revokeObjectURL(old); } catch { /* no-op */ }
      }
      return null;
    });
    setJudgeImageFile(null);
    setNewJudge((p) => ({ ...p, photo_url: '' }));
  };

  const addJudge = async () => {
    if (!newJudge.first_name) { toast.error(t('judges.fillRequired')); return; }
    try {
      let photo_url = "";
      if (judgeImageFile) { photo_url = await uploadImage(judgeImageFile); if (!photo_url) return; }
      await axios.post(`${API}/judges`, { ...newJudge, pin_code: newJudge.pin_code || "", photo_url });
      toast.success(t('judges.added'));
      setNewJudge({ judge_number: newJudge.judge_number + 1, first_name: "", pin_code: "" });
      setJudgeImageFile(null); setJudgeImagePreview(null);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || t('judges.errorAdding')); }
  };

  const deleteJudge = async (judgeNumber) => {
    try { await axios.delete(`${API}/judges/${judgeNumber}`); toast.success(t('judges.deleted')); loadData(); }
    catch { toast.error(t('judges.errorDeleting')); }
  };

  const deleteJudgePhoto = async (judge) => {
    if (!window.confirm(t('judges.confirmDeletePhoto'))) return;
    try {
      await axios.put(`${API}/judges/${judge.judge_number}`, { judge_number: judge.judge_number, first_name: judge.first_name, pin_code: judge.pin_code, photo_url: "" });
      toast.success(t('judges.photoDeleted')); loadData();
    } catch { toast.error(t('common.error')); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success(t('common.copied')); };

  // ===== Reorder judges (drag & drop + arrows) =====
  // The order stored in the `sort_order` field controls how judges appear
  // left-to-right on the Big Screen and everywhere else. Assignment is
  // separate — this only changes the display order.
  const orderedJudges = (judges || []).filter(j => j?.id).slice().sort((a, b) => {
    const oa = typeof a.sort_order === 'number' ? a.sort_order : a.judge_number;
    const ob = typeof b.sort_order === 'number' ? b.sort_order : b.judge_number;
    if (oa !== ob) return oa - ob;
    return (a.judge_number || 0) - (b.judge_number || 0);
  });
  const [dragIdx, setDragIdx] = useState(null);
  const persistOrder = async (list) => {
    const order = list.map(j => j.judge_number);
    try {
      await axios.put(`${API}/judges/reorder`, { order });
      // Invalidate the localStorage judges cache so any Display tab on the
      // same browser (rare, but possible for a manager multitasking) picks
      // up the new order on its next poll.
      invalidateCache('JUDGES');
      toast.success(t('judges.orderUpdated'));
      loadData();
    } catch { toast.error(t('common.error')); }
  };
  const moveJudge = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= orderedJudges.length || to >= orderedJudges.length) return;
    const list = orderedJudges.slice();
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    persistOrder(list);
  };
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (idx) => {
    if (dragIdx !== null && dragIdx !== idx) moveJudge(dragIdx, idx);
    setDragIdx(null);
  };

  return (
    <>
      <Card className="p-6 mb-6 glass">
        <h2 className="text-5xl font-bold mb-6">{t('judges.addTitle')}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input type="number" placeholder={t('judges.judgeNumber')} value={newJudge.judge_number} onChange={e => setNewJudge({...newJudge, judge_number: parseInt(e.target.value)})} min="1" max="5" data-testid="judge-number-input" className="text-2xl h-16" />
            <Input placeholder={t('judges.firstName')} value={newJudge.first_name} onChange={e => setNewJudge({...newJudge, first_name: e.target.value})} data-testid="judge-name-input" className="text-2xl h-16" />
            <Input placeholder={t('judges.pinCodeOptional')} value={newJudge.pin_code} onChange={e => setNewJudge({...newJudge, pin_code: e.target.value})} data-testid="judge-pin-input" className="text-2xl h-16" />
            <div>
              <PhotoPickerButtons
                onChange={handleJudgeImageChange}
                onDelete={(judgeImagePreview || newJudge.photo_url) ? handleDeletePhoto : undefined}
                testId="judge-photo"
              />
              <p className="text-base text-muted-foreground italic mt-1">{t('common.photoLabel')}</p>
            </div>
          </div>
          {judgeImagePreview && (<div className="flex items-center gap-3"><img src={judgeImagePreview} alt="Preview" className="w-16 h-16 rounded-full object-cover" /><span className="text-base text-muted-foreground">{t('common.imageReady')}</span></div>)}
          <Button onClick={addJudge} data-testid="add-judge-btn" className="text-2xl h-16"><Plus size={28} className="mr-2" /> {t('common.add')}</Button>
        </div>
      </Card>

      <div className="mb-2 text-sm text-muted-foreground">
        {t('judges.orderHint')}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {orderedJudges.map((judge, idx) => (
          <Card
            key={judge.id}
            className={`p-5 glass text-center relative transition-all ${dragIdx === idx ? 'ring-2 ring-primary/70' : ''}`}
            data-testid={`judge-card-${judge.judge_number}`}
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(idx)}
            onDragEnd={() => setDragIdx(null)}
          >
            <div className="absolute top-2 left-2 flex flex-col items-center gap-0.5">
              <button
                type="button"
                onClick={() => moveJudge(idx, idx - 1)}
                disabled={idx === 0}
                className="p-1 rounded hover:bg-muted disabled:opacity-30"
                aria-label="Move up"
                data-testid={`judge-move-up-${judge.judge_number}`}
              ><ChevronUp size={16} /></button>
              <span className="cursor-grab text-muted-foreground" title={t('judges.dragHint')}><GripVertical size={14} /></span>
              <button
                type="button"
                onClick={() => moveJudge(idx, idx + 1)}
                disabled={idx === orderedJudges.length - 1}
                className="p-1 rounded hover:bg-muted disabled:opacity-30"
                aria-label="Move down"
                data-testid={`judge-move-down-${judge.judge_number}`}
              ><ChevronDown size={16} /></button>
            </div>
            <div className="absolute top-2 right-2 text-[10px] uppercase font-bold text-muted-foreground bg-muted/60 rounded px-1.5 py-0.5">
              {t('judges.orderLabel')} {idx + 1}
            </div>
            <div className="relative">
              {judge.photo_url && (
                <>
                  <img src={getImageUrl(judge.photo_url)} alt={judge.first_name} className="w-32 h-32 rounded-full object-cover mx-auto mb-3" />
                  <Button variant="destructive" size="sm" onClick={() => deleteJudgePhoto(judge)} className="absolute top-0 right-1/4 w-8 h-8 p-0 rounded-full" title={t('judges.deletePhoto')}><Trash2 size={16} /></Button>
                </>
              )}
            </div>
            <div className="text-6xl font-bold mb-3">#{judge.judge_number}</div>
            <h3 className="font-bold text-3xl">{judge.first_name}</h3>
            <p className="text-xl text-muted-foreground">PIN: {judge.pin_code}</p>
            <div className="flex gap-1.5 justify-center mt-3 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => window.open(`${baseUrl}/event/${slug}/judge/${judge.judge_number}`, '_blank')} className="text-xs h-7 px-2 hover:bg-blue-600/20 hover:text-blue-400 hover:border-blue-500/30" data-testid={`open-judge-window-${judge.judge_number}`}><ExternalLink size={12} className="mr-1" /> {t('common.open')}</Button>
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${baseUrl}/event/${slug}/judge/${judge.judge_number}`)} className="text-xs h-7 px-2 hover:bg-purple-600/20 hover:text-purple-400 hover:border-purple-500/30" data-testid={`copy-judge-link-${judge.judge_number}`}><Link size={12} className="mr-1" /> {t('common.copy')}</Button>
              <Button variant="outline" size="sm" onClick={() => setQrJudge(judge)} className="text-xs h-7 px-2 hover:bg-green-600/20 hover:text-green-400 hover:border-green-500/30" data-testid={`qr-judge-${judge.judge_number}`}><QrCode size={12} className="mr-1" /> QR</Button>
            </div>
            <div className="flex gap-2 justify-center mt-2">
              <Button variant="outline" size="sm" onClick={() => openEditJudge(judge)} data-testid={`edit-judge-${judge.judge_number}`} className="hover:bg-muted"><Edit size={18} className="text-foreground" /></Button>
              <Button variant="destructive" size="sm" onClick={() => deleteJudge(judge.judge_number)} data-testid={`delete-judge-${judge.judge_number}`}><Trash2 size={18} /></Button>
            </div>
          </Card>
        ))}
      </div>
      <PhotoCropDialog
        file={pendingCropFile}
        onCancel={() => setPendingCropFile(null)}
        onConfirm={handleCropConfirm}
      />
    </>
  );
};
